import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter, Switch, Route, Redirect } from 'react-router-dom';
import { I18n } from 'react-redux-i18n';
import { Helmet } from 'react-helmet';
import queryString from 'query-string';
import loadable from '@loadable/component';
import pMinDelay from 'p-min-delay';

import LoaderSpinnerPage from 'components/LoaderSpinnerPage';

// Containers.
import AuthRequireContainer from 'containers/AuthRequireContainer';
import AuthRequireClearContainer from 'containers/AuthRequireClearContainer';
import AuthRequireContainer1 from 'containers/AuthRequireContainer1';
import AuthNotRequireContainer from 'containers/AuthNotRequireContainer';
import UserLoginDirectPage from 'containers/UserLoginDirectPage';
import UserLogoutPage from 'containers/UserLogoutPage';
import ErrorPage from 'containers/ErrorPage';
import NotFoundPage from 'containers/NotFoundPage';

import PageTitle from 'components/PageTitle';

// Components.
import AlertMessage from 'components/AlertMessage';

// Actions.
import * as AlertActions from 'actions/AlertActions';
import * as ConfigActions from 'actions/ConfigActions';
import * as AnalyticsActions from 'actions/AnalyticsActions';

// Utils.
import { isMobile } from 'utils';

// Styles.
import 'bootstrap/dist/css/bootstrap.min.css';
import 'styles';

const UserLoginPage = loadable(() => import('components/LoaderSpinnerPage'), {
  fallback: <LoaderSpinnerPage />,
});
const UserRegisterPage = loadable(() => import('components/LoaderSpinnerPage'), {
  fallback: <LoaderSpinnerPage />,
});
const UserResetPasswordPage = loadable(() => import('components/LoaderSpinnerPage'), {
  fallback: <LoaderSpinnerPage />,
});


const UserProfilePage = loadable(() => import('containers/UserProfilePage'), {
  fallback: <LoaderSpinnerPage />,
});

const UserProfilePageTitle = loadable(() => import('containers/UserProfilePageTitle'));

const UserPersonalDataPage = loadable(() => import('containers/UserPersonalDataPage'), {
  fallback: <LoaderSpinnerPage />,
});

const UserAccountDeletePage = loadable(() => import('containers/UserAccountDeleteMobilePage'), {
  fallback: <LoaderSpinnerPage />,
});

const UserAccountDeleteInfoPage = loadable(() => import('containers/UserAccountDeleteInfoMobilePage'), {
  fallback: <LoaderSpinnerPage />,
});

const UserAccountDeleteFinishPage = loadable(() => import('containers/UserAccountDeleteFinishMobilePage'), {
  fallback: <LoaderSpinnerPage />,
});

const UserAccountDeleteFinishRedirectPage = loadable(() => import('containers/UserAccountDeleteFinishRedirectMobilePage'), {
  fallback: <LoaderSpinnerPage />,
});

const PrivacyPolicyPage = loadable(() => import('containers/PrivacyPolicyPage'), {
  fallback: <LoaderSpinnerPage />,
});

const TermsPage = loadable(() => import('containers/TermsPage'), {
  fallback: <LoaderSpinnerPage />,
});

const SaveDataPage = loadable(() => import('containers/SaveDataPage'), {
  fallback: <LoaderSpinnerPage />,
});

const DashboardPage = loadable(() => import('containers/DashboardPage'), {
  fallback: <LoaderSpinnerPage />,
});

const DashboardPageTitle = loadable(() => import('containers/DashboardPageTitle'));

// const DashboardPageAdditionalTitle = loadable(() => import('containers/DashboardPageAdditionalTitle'));

const DataSourcesPage = loadable(() => import('containers/DataSourcesPage'), {
  fallback: <LoaderSpinnerPage />,
});

const DataSourcePage = loadable(() => import('containers/DataSourcePage'), {
  fallback: <LoaderSpinnerPage />,
});

const DataSourceMobileBackPage = loadable(() => import('containers/DataSourceMobileBackPage'), {
  fallback: <LoaderSpinnerPage />,
});

const DataSourcesPageTitle = loadable(() => import('containers/DataSourcesPageTitle'));

const LMSCoursesPage = loadable(() => import('containers/LMSCoursesPage'), {
  fallback: <LoaderSpinnerPage />,
});

const LMSCoursePageTitle = loadable(() => import('containers/LMSCoursePageTitle'));

const LMSCoursePageAdditionalTitle = loadable(() => import('containers/LMSCoursePageAdditionalTitle'));

const LMSCoursePage = loadable(() => import('containers/LMSCoursePage'), {
  fallback: <LoaderSpinnerPage />,
});

const LMSMaterialPage = loadable(() => import('containers/LMSMaterialPage'), {
  fallback: <LoaderSpinnerPage />,
});

const PaymentPage1 = loadable(() => import('containers/PaymentPage1'), {
  fallback: <LoaderSpinnerPage />,
});

const PaymentHistoryPage = loadable(() => import('containers/PaymentHistoryPage'), {
  fallback: <LoaderSpinnerPage />,
});

const PaymentCancelMobilePage = loadable(() => import('containers/PaymentCancelMobilePage'), {
  fallback: <LoaderSpinnerPage />,
});

const PaymentCancelInfoMobilePage = loadable(() => import('containers/PaymentCancelInfoMobilePage'), {
  fallback: <LoaderSpinnerPage />,
});

const Ouath2AuthorizeIftttPage = loadable(() => import('containers/Ouath2AuthorizeIftttPage'), {
  fallback: <LoaderSpinnerPage />,
});

const HelpRedirectPage = loadable(() => import('containers/HelpRedirectPage'), {
  fallback: <LoaderSpinnerPage />,
});

const HelpAuthRedirectPage = loadable(() => import('containers/HelpAuthRedirectPage'), {
  fallback: <LoaderSpinnerPage />,
});

function EmptyPage() {
  return <div></div>
}

@withRouter
@connect(
  state => ({
    auth: state.auth,
    i18n: state.i18n,
    alert: state.alert,
  }),
  dispatch => ({
    alertActions: bindActionCreators(AlertActions, dispatch),
    configActions: bindActionCreators(ConfigActions, dispatch),
  })
)
// @AppScripts
export default class AppContainer extends Component {

  static propTypes = {
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    children: PropTypes.element,
  };

  static childContextTypes = {
    destination: PropTypes.object,
    locale: PropTypes.string,
  };

  static contextTypes = {
    router: PropTypes.object,
  };

  constructor(props, context) {
    super();
    const { router: { route, history } } = context;

    this.isMobileStatus = isMobile();

    const query = queryString.parse(route.location.search);
    const isIFTTT = query.scope !== undefined && query.scope === 'ifttt';
    const mobileAppliedURLs = ['/privacy-policy', '/terms', '/user/account-delete', '/user/login-direct', '/share-measurement', '/referral'];

    this.isMobileRedirect = this.isMobileStatus === true && isIFTTT === false && mobileAppliedURLs.indexOf(route.location.pathname) === -1;
  }

  componentDidMount() {
    const { router: { route, history } } = this.context;
    const { auth } = this.props;
    const query = queryString.parse(route.location.search);

    if (route.location.pathname === '/') {
      // Backward compatibility. We should support pathnames with hash(#faq, #reports and etc).
      if (route.location.hash.length > 0) {
        const hashpath = route.location.hash.replace('#', '');
        history.push(hashpath);
      }
      else {
        const pathname = `${location.origin}/payments/plans/main/`;
        window.location.replace(pathname );
      }
    }

    if (auth.isAuthenticated === false) {
      AnalyticsActions.identifyUser();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { location } = nextProps;
    const { router: { history, route } } = this.context;
    if (location.pathname === '/') {
      // Backward compatibility. We should support pathnames with hash(#faq, #reports and etc).
      if (location.hash.length > 0) {
        const hashpath = location.hash.replace('#', '');
        history.push(hashpath);
      }
      else {
        // Redirect to dashboard page if user is authenticated otherwise redirect to login page.
        const pathname = '/dashboard';
        history.push({ pathname });
      }
    }
    if (this.props.auth.isAuthenticated !== nextProps.auth.isAuthenticated && nextProps.auth.isAuthenticated === false) {
      if (window.Intercom) {
        window.Intercom('shutdown');
        // delete window.Intercom;
        delete window.intercomSettings;
      }
      AnalyticsActions.identifyUser();
    }
    window.lastPathname = route.location.pathname;
  }

  getChildContext() {
    const { i18n } = this.props;
    const { router: { route } } = this.context;
    const query = queryString.parse(route.location.search);
    let destination = {
      pathname: '/dashboard',
      search: '',
    };
    if (query.destination) {
      destination.pathname = query.destination;
      delete query.destination;
    }
    if (Object.keys(query).length > 0) {
      destination.search = queryString.stringify(query);
    }
    return {
      destination: destination,
      locale: i18n.locale,
    }
  }

  clearAlertMessage = () => {
    this.props.alertActions.clearAlertMessage();
  };

  render() {
    const { router: { route: { location } } } = this.context;
    const query = queryString.parse(location.search);
    let dashboardNew = window.localStorage.getItem('dashboard_new');
    if (dashboardNew === null) {
      window.localStorage.setItem('dashboard_new', true);
      dashboardNew = true;
    }
    dashboardNew = JSON.parse(dashboardNew);




    return (
      <React.Fragment>
        <Helmet>
          <title>{I18n.t('Welltory')}</title>
        </Helmet>
        <div className="appWrapper">
          <Switch>
            <Route exact path="/" component={EmptyPage} />

            <AuthNotRequireContainer path="/user/login" component={UserLoginPage} />
            <AuthNotRequireContainer path="/user/register" component={UserRegisterPage} />

            <Route path="/user/login-direct" component={UserLoginDirectPage} />
            <Route path="/user/logout" component={UserLogoutPage} />
            
            <AuthRequireContainer1 path="/user/profile" component={UserProfilePage} title={<UserProfilePageTitle />}  />
            <AuthRequireContainer1 path="/user/personal" component={UserPersonalDataPage} title={<PageTitle title={I18n.t('Edit personal data')} />} />

            <AuthRequireContainer path="/user/account-delete" component={UserAccountDeletePage} />
            <AuthRequireContainer path="/user/account-delete-info" component={UserAccountDeleteInfoPage} />
            <AuthRequireContainer path="/user/account-delete-finish" component={UserAccountDeleteFinishPage} />
            <AuthNotRequireContainer path="/user/account-delete-finish-redirect" component={UserAccountDeleteFinishRedirectPage} />

            <Route path="/privacy-policy" component={PrivacyPolicyPage} />
            <Route path="/terms" component={TermsPage} />
            <Route path="/save-data" component={SaveDataPage} />

            <AuthRequireContainer path="/dashboard" component={DashboardPage} title={<DashboardPageTitle />} isFree={true} />

            <AuthRequireContainer path="/data-sources" component={DataSourcesPage} title={<DataSourcesPageTitle />} exact />
            <AuthRequireContainer path="/data-sources/:sourceSlug" component={DataSourcePage } title={null} exact />

            <AuthRequireContainer path="/data-sources-mobile-back/:sourceSlug" component={DataSourceMobileBackPage } title={null} isFree={true} exact />

            {/* <AuthRequireContainer path="/share" component={DashboardPage} title={<DashboardPageTitle />} isFree={true} /> */}

            <AuthRequireContainer path="/lms" component={LMSCoursesPage} title={<PageTitle title={I18n.t('Академия Welltory')} />} exact />
            <AuthRequireContainer path="/lms/courses" component={LMSCoursesPage} title={<PageTitle title={I18n.t('Академия Welltory')} />} exact />
            <AuthRequireContainer path="/lms/courses/:courseId" component={LMSCoursePage} title={<LMSCoursePageTitle />} additionTitle={<LMSCoursePageAdditionalTitle />} exact />
            <AuthRequireContainer path="/lms/courses/:courseId/:materialId" component={LMSMaterialPage} isFree={true} exact />
            <AuthRequireContainer path="/lms/materials/:materialId" component={LMSMaterialPage} isFree={true} exact />

            <Route path="/payment" component={PaymentPage1} exact />
            
            <Route path='/payment-method' component={() => {
                window.location.origin.includes('stage') ?
                window.location.origin.replace('-app', '-monocle') :
                window.location.origin;

                const monoclePaymentMethodHref = `${origin}/profile/payment-method`;

                window.location.replace(monoclePaymentMethodHref);
                return null;
            }}/>

            <AuthRequireContainer1 path="/payment-history" component={PaymentHistoryPage} title={<PageTitle title={I18n.t('Transaction history')} />} exact />

            <AuthRequireContainer path="/payment-cancel" component={PaymentCancelMobilePage} exact />
            <AuthRequireContainer path="/payment-cancel-info" component={PaymentCancelInfoMobilePage} exact />

            <AuthRequireClearContainer path="/oauth2/authorize/ifttt" component={Ouath2AuthorizeIftttPage} title={<PageTitle title={I18n.t('Authorize IFTTT')} />} />

            <AuthRequireClearContainer path="/help-redirect" component={HelpRedirectPage} />

            <AuthRequireClearContainer path="/help-auth-redirect" component={HelpAuthRedirectPage} />

            <AuthRequireContainer path="/error" component={ErrorPage} theme="ink" isFree={true} />

            <AuthRequireContainer path="/page-404" component={NotFoundPage} status={404} theme="ink" isFree={true} />

            <AuthRequireContainer component={NotFoundPage} status={404} theme="ink" isFree={true} />
          </Switch>
        </div>
        {this.props.alert.message &&
          <AlertMessage message={this.props.alert.message} status={this.props.alert.status} clearAlertMessage={this.clearAlertMessage} />
        }
      </React.Fragment>
    );
  }

}
